import URL from '@/common/app/utils/urlsUtils';
import { getDestinationList } from '@/common/service/api/Destination/Destination';
import { getRedirectsPath } from '@/common/service/api/Redirects/getRedirectsPath';
import { getDestination } from '@/entities/Attractions/service/Creators';
import { IDestinationList } from '@/entities/Destination/domain/Destination.domain';
import { TLandmarksShort } from '@/screens/LandmarkMainPage/domain/Landmarks.domain';
import { fetchLandmarksByCityId } from '@/screens/LandmarkMainPage/service/fetchLandmarksByCityId';
import {
    LandmarksListPageContainer,
    TLandmarksListPageProps,
} from '@/screens/LandmarksListPage/ui/LandmarkDestinationPage';
import { GetStaticProps } from 'next';

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

type PageStaticProps = {
    state: string;
    destination: string;
};

export const getStaticProps: GetStaticProps<TLandmarksListPageProps, PageStaticProps> = async ({
    params,
}) => {
    try {
        if (!params) {
            throw new Error(`Page /destination/[state]/[destination]/landmarks have a wrong params: ${params}`);
        }

        let currentDestination = getDestination(params.destination);

        if (!currentDestination) {
            const fullDestinations = await getDestinationList();
            const currentDestinationPath = params.destination.toLowerCase();

            currentDestination = fullDestinations.items.find(({ slug }) => slug.toLowerCase() === currentDestinationPath) as
                | IDestinationList
                | undefined;
        }

        const currentPath = `/destination/${params.state}/${params.destination}/landmarks/`;

        if (!currentDestination) {
            const redirectPath = await getRedirectsPath(currentPath);

            if(redirectPath === '/404/') {
                return {
                    notFound: true,
                };
            }
            
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect:{
                    destination: redirectPath.toLowerCase(),
                    statusCode: 301,
                },
            };
        }

        const rightPath = `/destination/${currentDestination.state.code}/${currentDestination.slug}/landmarks/`.toLowerCase();

        if(currentPath !== rightPath) {
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect:{
                    destination: rightPath,
                    statusCode: 301,
                },
            };
        };

        const landmarks = (await fetchLandmarksByCityId(
            currentDestination.id,
            true
        )) as TLandmarksShort;

        const heroImages = [
            `${URL.image}/destination/${currentDestination.id}/${currentDestination.slug}.jpg`,
            `${URL.image}/destination/${currentDestination.id}/${currentDestination.slug}.webp`,
        ];

        const breadcrumbs = [
            {
                url: '/destination/',
                title: 'Destination',
            },
            {
                url: `/destination/${currentDestination.state.code.toLowerCase()}/`,
                title: currentDestination.state.name,
            },
            {
                url: rightPath.replace('landmarks/', ''),
                title: currentDestination.name,
            },
            {
                url: `/destination/${currentDestination.state.code}/${currentDestination.slug}/landmarks/`,
                title: 'Landmarks',
                unlink: true,
            },
        ];

        return {
            revalidate: Number(process.env.CACHE_ENV),
            props: {
                heroImages,
                landmarks,
                breadcrumbs,
                destinationSlug: currentDestination.slug,
                destinationName: currentDestination.name,
                destinationId: currentDestination.id,
                destinationCode: currentDestination.state.code,
                destinationStateName: currentDestination.state.name,
            },
        };
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export default LandmarksListPageContainer;
